import { useContext, useState, useEffect } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from 'react-router-dom';

import modalRoutes from '@/modalRoutes'
import { AuthContext } from '@/contexts/AuthContext'
import Button from '@/components/Button'
import Link from '@/components/Link'
import { convertAmount } from '@/utils/currencyResolver'
import { classNames } from '@/utils/classNames'
import { HealthBar } from '@/modals/Duel/StartDuel'
import errorToast from '@/utils/errorToast'

const getUserLink = (user) => {
  if (user?.privacy?.stats) return (
    <p className="flex items-center justify-center space-x-1 text-gray-300 mb-3">
      <span className="font-medium">Hidden</span>
    </p>
  )
  return (
    <Link className="flex items-center justify-center space-x-1 mb-3" to={{ search: '&modal=user&name=' + user?.username }}>
      <span className="text-white font-medium">{user?.username}</span>
    </Link>
  )
}

export default function Duel({ stake }) {
  const { auth, userPrimaryAccount } = useContext(AuthContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const functions = getFunctions()
  const acceptDuel = httpsCallable(functions, 'acceptDuel');
  const deleteDuel = httpsCallable(functions, 'cancelDuel');
  const router = useNavigate();
  const [joinedDoc, setJoinedDoc] = useState(null)
  const [createdDoc, setCreatedDoc] = useState(null)

  const handleAccept = async () => {
    try {
      setIsSubmitting(true)
      await acceptDuel({ duel: stake.id }).then(() => {
        router({ search: `&modal=startDuel&id=${stake.id}` })
      })
    } catch (error) {
      errorToast(error)
    }
    setIsSubmitting(false)
  }

  const handleDelete = async () => {
    try {
      setIsSubmitting(true)
      await deleteDuel(stake.id)
    } catch (error) {
      console.log(error)
    }
    setIsSubmitting(false)
  }

  const loadPlayers = async () => {
    // const joinedDocData = await stake.joinedUser.get()
    if (stake?.created && !createdDoc) {
      const createdDocData = await stake.createdUser.get()
      setCreatedDoc(createdDocData)
    }
    if (stake?.joined && !joinedDoc) {
      const joinedDocData = await stake.joinedUser.get()
      setJoinedDoc(joinedDocData)
    }
  }

  useEffect(() => {
    if (!stake.joined) {
      setJoinedDoc(null)
    }
    loadPlayers()
  }, [stake])

  return (
    <div key={stake.id} className="relative group flex">
      {(stake.joined == auth.uid || stake.created == auth.uid) && stake.joined && !stake.hidden && (
        <>
          <div className="absolute rounded inset-x-0 top-[-1px] bottom-[-1px] bg-purple-600 animate-activeDuel"></div>
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-black rounded bg-opacity-50 transition duration-300">
            <Button modifier="secondary" rounded="rounded-full" link to={`${modalRoutes.START_DUEL.search}&id=${stake.id}`} className="py-2.5 px-6">View Duel</Button>
          </div>
        </>
      )}
      <div className="w-full flex relative p-4 bg-gray-900 rounded shadow-lg transition duration-300">
        <div className="flex-1">
          {createdDoc ? <PlayerCard type="created" stake={stake} player={createdDoc} /> : <SkeletonTheme baseColor="#141524" highlightColor="#191d2c" ><Skeleton height={176} /></SkeletonTheme>}
        </div>
        <div className={classNames("flex flex-col items-center", stake.allowedWeapons.includes("0") ? 'px-4' : 'px-2')}>
          <div className="flex flex-1 w-px bg-gray-700"></div>
          {!stake.allowedWeapons.includes("0") && (
            <div className="my-2">
              {stake.allowedWeapons.map(weapon => (
                <img key={weapon} className="w-10" src={`/weapons/${weapon}.png`} alt="" />
              ))}
            </div>
          )}
          <div className="flex flex-1 w-px bg-gray-700"></div>
        </div>
        <div className="flex-1">
          {stake.joined ? (
            joinedDoc ? <PlayerCard type="joined" stake={stake} player={joinedDoc} /> : <SkeletonTheme baseColor="#141524" highlightColor="#191d2c" ><Skeleton height={176} /></SkeletonTheme>
          ) : (
            <div className={classNames('flex-1 flex h-full justify-center items-center p-3 bg-gray-900 rounded', stake.winner ? stake.winner == stake.joined ? 'opacity-100' : 'opacity-50' : '')}>
              <div className="w-full flex flex-col justify-center self-center">
                <div className="flex justify-center flex-1">
                  {stake.createdUid == auth?.uid ? (
                    <Button rounded="rounded-full" onClick={handleDelete} className="px-2 py-2 w-full" modifier="danger">
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </span>
                      ) : (
                        <span>Cancel</span>
                      )}
                    </Button>
                  ) : (
                    userPrimaryAccount ? (
                      <Button modifier="secondary" rounded="rounded-full" onClick={handleAccept} className="px-2 w-full py-2.5">
                        {isSubmitting ? (
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <span>Challenge</span>
                        )}
                      </Button>
                    ) : (
                      <Button modifier="secondary" rounded="rounded-full" link to={modalRoutes.LINK_ACCOUNT} className="px-2 py-2 w-full text-center">Link RSN</Button>
                    )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div >
  )
}

export const PlayerCard = ({ stake, player, type }) => {
  const [playerData, setPlayerData] = useState({ id: player?.id, ...player?.data() })
  const [avatarLoaded, setAvatarLoaded] = useState(false)

  return (
    <div className={classNames('w-full flex px-3 relative py-4 bg-gray-800 bg-opacity-60 rounded', stake.winner ? stake.winner == stake[type] ? 'opacity-100' : 'opacity-50' : '')}>
      {!playerData?.privacy?.stats && <span className={classNames('absolute top-1.5 left-1.5 font-semibold text-xs', playerData.streak > 0 ? 'text-green-500' : playerData.streak < 0 ? 'text-red-500' : 'text-gray-300')}>{playerData.streak > 0 ? '+' : ''}{playerData.streak}</span>}

      <div className="w-full flex flex-col">
        {!avatarLoaded && <div className="flex justify-center mb-3"><Skeleton circle height={61} width={61} /></div>}
        <img src={playerData?.privacy?.stats ? 'https://storage.googleapis.com/duelarenagg-b7872.appspot.com/avatars/default.png' : playerData?.avatarUrl} onLoad={() => setAvatarLoaded(true)} className={classNames('mx-auto h-16 w-16 rounded-full object-cover shadow-lg mb-3', avatarLoaded ? 'flex' : 'hidden')} />
        {getUserLink(playerData)}
        {stake.duelStart && (
          <div className="rounded overflow-hidden mb-3">
            <HealthBar damage={99 - (type == "created" ? stake.createdHp : stake.joinedHp)} />
          </div>
        )}
        <p className={classNames('mt-auto rounded flex justify-center items-center text-center bg-gray-900 py-1.5 shadow-inner font-medium', stake.winner ? stake.winner == stake[type] ? 'text-green-600' : 'text-red-500' : 'text-green-600')}>
          <img className="w-6" src="/icons/GP_COIN_03.svg" alt="" />
          {stake.winner ? stake.winner == stake[type] ? convertAmount(stake.payout) : convertAmount(-stake.wager) : convertAmount(stake.wager)}
        </p>
      </div>
    </div>
  )
}